/* Rapportages / Overzichten — download CSV of print voor alle kern-overzichten.
   Zichtbaar voor admin (superadmin/locatiemanager/financieel). Print-view
   sluit sidebar en topbar uit. */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth, can } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { localToday } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FileDown, Printer, Baby, Users, UserCog, CalendarCheck2, Receipt, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const REPORTS = [
  { key: "kinderen", label: "Kinderen", desc: "Alle geregistreerde kinderen met locatie, groep en startdatum.", icon: Baby, tone: "sage" },
  { key: "ouders", label: "Ouders", desc: "Contactgegevens, IBAN en machtiging.", icon: Users, tone: "sage" },
  { key: "medewerkers", label: "Medewerkers", desc: "Personeel met functie, contracturen en dienstverband.", icon: UserCog, tone: "terra" },
  { key: "aanwezigheid", label: "Aanwezigheid vandaag", desc: "Kinderen op de groep — per datum en dagdeel.", icon: CalendarCheck2, tone: "sage", dateParam: true },
  { key: "facturen", label: "Facturen (financieel)", desc: "Wie heeft betaald, wie niet — filter op status en jaar.", icon: Receipt, tone: "terra", financial: true },
];

const STATUS_OPTS = ["concept", "verzonden", "betaald", "overtijd", "gecrediteerd"];

export default function RapportagesPage() {
  const { user } = useAuth();
  const allowed = can(user, "reports", "read");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [datum, setDatum] = useState(localToday());
  const [factStatus, setFactStatus] = useState("all");
  const [factYear, setFactYear] = useState(new Date().getFullYear());
  const [busy, setBusy] = useState(null); // key of report currently downloading

  const load = () => {
    if (!allowed) { setLoading(false); return; }
    setLoading(true);
    api.get("/reports/summary")
      .then(r => setSummary(r.data))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [allowed]);

  const buildUrl = (key) => {
    if (key === "aanwezigheid") return `/reports/aanwezigheid.csv?datum=${datum}`;
    if (key === "facturen") {
      const p = new URLSearchParams();
      if (factStatus !== "all") p.set("status", factStatus);
      if (factYear) p.set("year", String(factYear));
      const s = p.toString();
      return `/reports/facturen.csv${s ? `?${s}` : ""}`;
    }
    return `/reports/${key}.csv`;
  };

  const downloadCSV = async (key, label) => {
    setBusy(key);
    try {
      const res = await api.get(buildUrl(key), { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${key}-${localToday()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${label} — CSV gedownload`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Download mislukt");
    } finally {
      setBusy(null);
    }
  };

  const printReport = (key) => {
    // Open printbare voorbeeldweergave in nieuw venster met CSV → HTML tabel
    setBusy(key);
    api.get(buildUrl(key), { responseType: "text" })
      .then(res => {
        const html = csvToPrintableHtml(res.data, REPORTS.find(r => r.key === key)?.label || key);
        // Gebruik een sandboxed Blob-URL i.p.v. document.write om XSS-injectie
        // via CSV-cellen te voorkomen. Server-side HTML wordt behandeld als
        // untrusted content en volledig in een aparte tab geïsoleerd.
        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const w = window.open(url, "_blank", "width=1024,height=800,noopener,noreferrer");
        if (!w) { toast.error("Popup-blocker actief — sta popups toe."); URL.revokeObjectURL(url); return; }
        // Cleanup na print-dialoog geopend is
        setTimeout(() => { try { w.focus(); w.print(); } catch (err) { console.debug("Print-dialog kon niet openen:", err); } }, 800);
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(e => toast.error(formatApiError(e.response?.data?.detail) || "Print mislukt"))
      .finally(() => setBusy(null));
  };

  const TONE = {
    sage: "border-l-[color:var(--sage)]",
    terra: "border-l-[color:var(--terracotta)]",
  };

  const kpis = useMemo(() => summary && ([
    { label: "Kinderen", value: summary.kinderen, tone: "sage", icon: Baby },
    { label: "Ouders", value: summary.ouders, tone: "sage", icon: Users },
    { label: "Medewerkers", value: summary.medewerkers, tone: "terra", icon: UserCog },
    { label: "Aanwezig vandaag", value: summary.aanwezig_vandaag, tone: "sage", icon: CalendarCheck2, helper: `${summary.afwezig_vandaag} afwezig` },
    { label: "Betaalde facturen", value: summary.betaalde_facturen, tone: "sage", icon: Receipt },
    { label: "Open facturen", value: summary.open_facturen, tone: "terra", icon: Receipt, helper: `€ ${summary.open_bedrag.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}` },
  ]), [summary]);

  if (!allowed) {
    return (
      <div className="max-w-lg" data-testid="rapportages-forbidden">
        <PageHeader title="Geen toegang" subtitle="U heeft geen rechten om rapportages te bekijken." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Rapportages"
        subtitle="Live overzichten van kinderen, ouders, medewerkers, aanwezigheid en financiën — download als CSV of print."
        testid="rapportages-header"
        actions={
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reports-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Ververs
          </Button>
        }
      />

      {/* KPI strip */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-6 sm:mb-8" data-testid="reports-kpis">
          {kpis.map(k => (
            <Card key={k.label} className={`p-4 border-l-4 ${TONE[k.tone]} bg-white shadow-none`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium truncate">{k.label}</div>
                  <div className="font-display text-2xl tracking-tight mt-1 text-neutral-900 truncate">{k.value}</div>
                  {k.helper && <div className="text-[11px] text-neutral-500 mt-0.5 truncate">{k.helper}</div>}
                </div>
                <k.icon className="h-4 w-4 text-neutral-300 shrink-0" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Rapport-cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        {REPORTS.map(r => {
          const Ic = r.icon;
          return (
            <Card key={r.key} className={`p-5 sm:p-6 bg-white shadow-none border-l-4 ${TONE[r.tone]} flex flex-col`} data-testid={`report-card-${r.key}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-md flex items-center justify-center ${r.tone === "sage" ? "bg-[color:var(--sage-100)] text-[color:var(--sage)]" : "bg-orange-50 text-[color:var(--terracotta)]"}`}>
                  <Ic className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-display text-lg tracking-tight truncate">{r.label}</div>
                  {summary && r.key !== "aanwezigheid" && r.key !== "facturen" && (
                    <Badge variant="outline" className="text-[10px] mt-0.5">
                      {summary[r.key === "medewerkers" ? "medewerkers" : r.key === "ouders" ? "ouders" : "kinderen"]} records
                    </Badge>
                  )}
                </div>
              </div>
              <p className="text-sm text-neutral-600 mb-4 flex-1">{r.desc}</p>

              {r.dateParam && (
                <div className="mb-4">
                  <Label className="text-xs">Datum</Label>
                  <Input type="date" value={datum} onChange={e => setDatum(e.target.value)}
                    className="mt-1" data-testid="aanwezigheid-datum" />
                </div>
              )}
              {r.financial && (
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={factStatus} onValueChange={setFactStatus}>
                      <SelectTrigger className="mt-1" data-testid="facturen-status-filter"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="all">Alle statussen</SelectItem>
                        {STATUS_OPTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Jaar</Label>
                    <Input type="number" value={factYear} onChange={e => setFactYear(parseInt(e.target.value) || "")}
                      className="mt-1" data-testid="facturen-year-filter" />
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-auto">
                <Button
                  size="sm"
                  onClick={() => downloadCSV(r.key, r.label)}
                  disabled={busy === r.key}
                  data-testid={`download-${r.key}`}
                  style={{ background: "var(--sage)" }}
                  className="text-white flex-1 min-w-[130px]"
                >
                  <FileDown className="h-4 w-4 mr-1.5" />
                  {busy === r.key ? "Bezig…" : "Download CSV"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => printReport(r.key)}
                  disabled={busy === r.key}
                  data-testid={`print-${r.key}`}
                  className="flex-1 min-w-[110px]"
                >
                  <Printer className="h-4 w-4 mr-1.5" />
                  Print
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="text-xs text-neutral-500 mt-6 sm:mt-8 max-w-3xl">
        <strong>Admin-tip:</strong> alle wijzigingen aan facturen, contracten en historische data
        (ook maanden of jaren terug) worden gelogd in het Logboek met datum, gebruiker en reden.
        Ga naar <em>Facturering</em> om betaalstatus per factuur te wijzigen (ook voor oude periodes).
      </div>
    </div>
  );
}

// ---------- CSV → printable HTML ----------
function csvToPrintableHtml(csvText, title) {
  // strip BOM
  if (csvText.charCodeAt(0) === 0xFEFF) csvText = csvText.slice(1);
  const lines = csvText.split(/\r?\n/).filter(Boolean);
  const parseLine = (line) => {
    // simpele split op ; (we schrijven met QUOTE_MINIMAL en `;` delimiter)
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQ = !inQ; continue; }
      if (ch === ';' && !inQ) { out.push(cur); cur = ""; continue; }
      cur += ch;
    }
    out.push(cur);
    return out;
  };
  const headers = parseLine(lines[0] || "");
  const rows = lines.slice(1).map(parseLine);
  const now = new Date().toLocaleString('nl-NL');
  return `<!doctype html><html lang="nl"><head><meta charset="utf-8">
    <title>${escapeHtml(title)} — Webster</title>
    <style>
      body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1a1a1a;padding:32px;margin:0;}
      header{display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid #2D4A3E;padding-bottom:12px;margin-bottom:24px;}
      h1{margin:0;font-size:22px;color:#2D4A3E;}
      .meta{font-size:11px;color:#666;}
      table{width:100%;border-collapse:collapse;font-size:12px;}
      th{text-align:left;padding:8px 10px;background:#f6f4ee;border-bottom:1px solid #ddd;font-weight:600;color:#2D4A3E;text-transform:uppercase;font-size:10px;letter-spacing:.5px;}
      td{padding:8px 10px;border-bottom:1px solid #eee;}
      tr:nth-child(even) td{background:#fafafa;}
      footer{margin-top:32px;font-size:10px;color:#888;text-align:center;}
      @media print{ body{padding:16px;} header{margin-bottom:12px;} }
    </style></head><body>
    <header>
      <div>
        <h1>${escapeHtml(title)}</h1>
        <div class="meta">${rows.length} records</div>
      </div>
      <div class="meta">
        Webster<br>${escapeHtml(now)}
      </div>
    </header>
    <table>
      <thead><tr>${headers.map(h => `<th>${escapeHtml(h)}</th>`).join("")}</tr></thead>
      <tbody>${rows.map(r => `<tr>${r.map(c => `<td>${escapeHtml(c)}</td>`).join("")}</tr>`).join("")}</tbody>
    </table>
    <footer>Gegenereerd via Webster (Powered by Webster) · ${escapeHtml(now)}</footer>
    </body></html>`;
}

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
